export type TypedBuyRateInputs = { typedBuyRatesByTerm?: Record<number, number> };

/**
 * A buy rate the user typed into the Buy Rate field, by term. The same per-term slot is also written by the payment grid,
 * the held-values restore and the alternate structure card, so a stored buy rate alone does not say who chose it. The
 * marker is kept on the pencilled inputs node, which the save payload does not copy, so it never reaches the deal.
 */
export const getTypedBuyRates = (inputs: object | undefined): Record<number, number> =>
    (inputs as TypedBuyRateInputs | undefined)?.typedBuyRatesByTerm ?? {};

export const withTypedBuyRate = (inputs: object | undefined, months: number, buyRate: number): Required<TypedBuyRateInputs> => ({
    typedBuyRatesByTerm: { ...getTypedBuyRates(inputs), [months]: buyRate }
});

export const withoutTypedBuyRate = (inputs: object | undefined, months: number): TypedBuyRateInputs => {
    const typed = getTypedBuyRates(inputs);
    if (typed[months] === undefined) return {};
    return { typedBuyRatesByTerm: Object.fromEntries(Object.entries(typed).filter(([term]) => Number(term) !== months)) };
};

/**
 * A write that is not the user typing replaces the stored buy rate. A recorded rate that no longer matches it is stale,
 * and would match again by accident if a later write copied the same number back.
 */
export const withoutStaleTypedBuyRate = (
    inputs: object | undefined,
    months: number,
    buyRate: number | undefined
): TypedBuyRateInputs => (getTypedBuyRates(inputs)[months] === buyRate ? {} : withoutTypedBuyRate(inputs, months));

type BuyRateByTerm = Record<string, { buyRate?: number | null } | undefined>;

/**
 * Switching between money factor and APR converts every stored buy rate. A recorded rate follows the stored rate it
 * matched, so the match survives the conversion without a second conversion of its own.
 */
export const withTypedBuyRatesFollowing = (
    inputs: object | undefined,
    previous: BuyRateByTerm,
    next: BuyRateByTerm
): TypedBuyRateInputs => {
    const typed = getTypedBuyRates(inputs);
    if (Object.keys(typed).length === 0) return {};
    const followed = Object.entries(typed)
        .filter(([term, rate]) => previous[term]?.buyRate === rate && next[term]?.buyRate != null)
        .map(([term]) => [term, next[term]!.buyRate as number]);
    return { typedBuyRatesByTerm: Object.fromEntries(followed) };
};
